import { ExtensionConnectionResponseSchema, connectionVersionCompatible, type ExtensionConnectionRequest, type ExtensionConnectionResponse, type ExtensionConnectionStatus } from "@cross-border-agent/contracts/extension-connection";

export function createExtensionConnectionClient(target: Window = window, timeout = 10_000) {
  const pending = new Map<string, { action: string; timer: ReturnType<typeof setTimeout>; resolve: (value: ExtensionConnectionResponse["result"]) => void; reject: (error: Error) => void }>();
  let disposed = false;
  const listener = (event: MessageEvent) => {
    if (event.source !== target || event.origin !== target.location.origin) return;
    const parsed = ExtensionConnectionResponseSchema.safeParse(event.data);
    if (!parsed.success) return;
    const { id, result } = parsed.data, item = pending.get(id);
    if (!item || (result.kind !== "error" && result.kind !== (item.action === "status" ? "status" : "opened"))) return;
    clearTimeout(item.timer); pending.delete(id); item.resolve(result);
  };
  target.addEventListener("message", listener);
  return {
    request(action: ExtensionConnectionRequest["action"]): Promise<ExtensionConnectionResponse["result"]> {
      if (disposed) return Promise.reject(new Error("DISPOSED"));
      return new Promise((resolve, reject) => {
        const id = crypto.randomUUID();
        const timer = setTimeout(() => { pending.delete(id); reject(new Error("CONNECTION_TIMEOUT")); }, timeout);
        pending.set(id, { action, timer, resolve, reject });
        target.postMessage({ source: "xiaoman-web", protocol: 1, id, action } satisfies ExtensionConnectionRequest, target.location.origin);
      });
    },
    dispose() {
      disposed = true; target.removeEventListener("message", listener);
      for (const item of pending.values()) { clearTimeout(item.timer); item.reject(new Error("DISPOSED")); }
      pending.clear();
    },
  };
}

function canonicalOrigin(value: string) {
  try { const url = new URL(value); if (url.hostname === "localhost") url.hostname = "127.0.0.1"; return url.origin; } catch { return ""; }
}
export function connectionView(status: ExtensionConnectionStatus | undefined, expectedApi: string, checking = false) {
  if (checking) return { key: "checking", title: "正在检测插件连接", detail: "正在核对当前浏览器里的插件、登录和服务状态。" };
  if (!status) return { key: "disconnected", title: "未检测到插件连接", detail: "不代表插件未安装。请在安装小满的 Chrome 或 Edge 中打开本页；旧插件更新后需刷新页面。" };
  if (!connectionVersionCompatible(status.version)) return { key: "update", title: "插件需要更新", detail: "当前插件尚不支持此连接协议，请更新后刷新本页。" };
  if (canonicalOrigin(status.apiOrigin) !== canonicalOrigin(expectedApi)) return { key: "environment", title: "网页与插件的服务环境不一致", detail: "请使用本页提供的对应版本；如手动修改过插件服务地址，请在插件设置里核对。" };
  if (status.service === "unavailable") return { key: "service", title: "插件已连接，服务暂时无法使用", detail: "服务或账户检查未完成。可以重试；本次检测不会清除你的登录信息。" };
  if (status.service === "unconfigured") return { key: "configuration", title: "插件已连接，AI 服务尚未配置完整", detail: "服务已响应，但模型配置不完整，需要管理员完成配置后再试。" };
  if (status.service === "demo") return { key: "demo", title: "插件已连接，当前为模拟模式", detail: "可以打开插件体验流程，但模拟回复不代表正式 AI 服务可用。" };
  if (status.auth === "signed_out") return { key: "login", title: "插件已连接，等待登录同步", detail: "新版插件与网页可自动同步登录。在任一端登录后，另一端无需重复登录；若未同步，请检查插件版本和服务环境。" };
  if (status.auth === "refresh_required") return { key: "renew", title: "插件已连接，登录待续期", detail: "请打开小满，让插件续期已有登录；如续期失败，再按提示重新登录。" };
  if (status.auth === "expired") return { key: "expired", title: "插件已连接，登录已失效", detail: "请打开小满重新登录，随后返回本页检测。" };
  if (status.auth === "unknown") return { key: "unknown", title: "插件已连接，登录状态待确认", detail: "登录状态可能刚发生变化，请重新检测。" };
  return { key: "ready", title: "可以使用小满了", detail: "连接、登录及服务基础检查已通过。打开小满后可提问；实际回答仍取决于模型服务、网络和可用积分。" };
}
